import classNames from 'classnames';

import { useCollectionSurtitle } from '../../hooks/useSurtitle';

import { useDefaultBrand } from '../../contexts/SiteContext';
import HeaderAd from '../ads/HeaderAd';
import Blocks from '../blocks/Blocks';
import CollectionSerieHeader from '../headers/CollectionSerieHeader';
import InfiniteItemsBlocks from '../lists/InfiniteItemsBlocks';
import DocumentTheme from '../themes/DocumentTheme';
import ListTitle from '../typography/ListTitle';

import styles from '../../styles/collections/serie-collection.module.css';

// import serieBackground from '../../assets/img/serie_background.png';

interface SerieCollectionProps {
    // Ad space at the top of the page (`HeaderAd`), on the background of the page
    withHeaderAd?: boolean;
    slug: string;
    id?: string | null;
    type?: string | null;
    surtitle?: string | null;
    title?: string | null;
    description?: string | null;
    header?: CollectionHeader | null;
    metadata?: CollectionMetadata | null;
    access?: Access | null;
    theme?: Theme | null;
    image?: Image | null;
    trailer?: Video | null;
    itemsOrder?: 'new' | 'old' | null;
    seasons?: CollectionSnippet[] | null;
    children?: CollectionSnippet[] | null;
    currentDocument?: FullDocument | null;
    current?: boolean;
    withoutHeaderImage?: boolean;
    className?: string;
}

function SerieCollection({
    withHeaderAd = false,
    id: collectionId = null,
    slug = null,
    type = null,
    surtitle = null,
    title = null,
    description = null,
    header = null,
    metadata,
    access,
    theme,
    image,
    trailer,
    itemsOrder,
    seasons = null,
    children = null,
    currentDocument = null,
    current = false,
    withoutHeaderImage = false,
    className,
}: SerieCollectionProps) {
    const { brands, itemsCount } = metadata || {};
    const { name: themeName = null } = theme || {};
    const defaultBrand = useDefaultBrand();
    const { handle: defaultBrandHandle = 'urbania' } = defaultBrand || {};
    const [brand = null] = brands || [];
    const { handle: brandHandle = defaultBrandHandle } = brand || {};

    const finalSurtitle = useCollectionSurtitle({
        type,
        surtitle,
        metadata,
        seasons,
        withSerieFormat: true,
    });

    const isPodcast = type === 'serie_podcast' || type === 'season_podcast';
    const hasSeasons = seasons !== null && seasons.length > 0;
    const hasChildren = children !== null && children.length > 0;
    const order = itemsOrder || (isPodcast ? 'new' : 'old');

    const childrenBlocks = hasSeasons
        ? seasons
              .sort(({ date: aDate }, { date: bDate }) =>
                  order === 'new' ? (aDate > bDate ? -1 : 1) : aDate > bDate ? 1 : -1,
              )
              .map(({ id: seasonId, title: seasonTitle }) => ({
                  type: 'documents-row',
                  presentation: 'grid',
                  title: <ListTitle className={styles.seasonTitle}>{seasonTitle}</ListTitle>,

                  cardWithout: [
                      'serie',
                      'collection',
                      'category',
                      !isPodcast ? 'time' : null,
                      'type-icon',
                      'author',
                  ].filter((n) => n !== null),
                  cardWith: ['episode'],
                  query: {
                      collection: seasonId,
                      with_unindexed: true,
                      order: itemsOrder || 'old',
                  },
                  loading: 'lazy',

                  count: null,
                  placeholderCount: 4,
                  multiplier: 8,
                  // minColumns: 1,
                  ...(isPodcast
                      ? {
                            //   cardType: 'horizontal',
                            //   cardTheme: 'box-translucent-dark',
                            //   cardRatio: 'square',
                            //   cardSize: 'medium',
                            //   columns: 1,

                            cardType: 'horizontal',
                            cardTheme: 'box-translucent',
                            cardRatio: 'square',
                            cardSize: 'medium',
                            autoColumns: true,
                            cardWidth: 300,
                        }
                      : {
                            //   cardType: 'over',
                            //   cardSize: 'big',
                            //   cardTheme: 'box-translucent',
                            cardTheme: 'rounded',
                            cardRatio: 'horizontal',
                            cardWidth: 300,
                        }),
              }))
        : (children || []).map(({ id: childId, slug: childSlug, ...child }) => ({
              type: 'collection-row',
              presentation: 'grid',
              slug: childSlug,

              cardWithout: ['serie', 'collection', 'category'],
              cardWith: ['episode'],
              withoutSummarySponsor: true,
              summaryImagePosition: 'left',
              // query: {
              //     collection: childId,
              // },
              loading: 'lazy',
          }));

    return (
        <DocumentTheme
            id={`collection-${collectionId}`}
            // backgroundImage={
            //     backgroundColor !== null
            //         ? {
            //               url: serieBackground,
            //           }
            //         : null
            // }
            {...theme}
            containerClassName={styles.container}
            className={classNames([
                styles.container,
                { [styles.withDocument]: currentDocument !== null, [styles.isPodcast]: isPodcast },
                className,
            ])}
            data-brand={brandHandle}
            data-theme={themeName}
        >
            {withHeaderAd ? <HeaderAd /> : null}
            <CollectionSerieHeader
                slug={slug}
                title={title}
                surtitle={finalSurtitle}
                description={description}
                metadata={metadata}
                access={access}
                background={
                    type === 'magazine'
                        ? {
                              color: '#fff',
                          }
                        : null
                }
                {...header}
                trailer={trailer}
                type={type}
                image={header !== null ? header.image || image : image}
                withoutImage={withoutHeaderImage}
                placeholder={collectionId === null}
                currentDocument={currentDocument}
                className={styles.header}
                imageClassName={styles.headerImage}
            />
            <main className={styles.content}>
                {childrenBlocks !== null && childrenBlocks.length > 0 ? (
                    <Blocks
                        blocks={childrenBlocks}
                        className={styles.blocks}
                        // withoutMaxWidth
                        blockClassName={classNames([
                            styles.row,
                            {
                                [styles.season]: hasSeasons,
                                [styles.podcast]: isPodcast,
                            },
                        ])}
                    />
                ) : null}
                {!hasChildren ? (
                    <InfiniteItemsBlocks
                        query={
                            hasSeasons
                                ? {
                                      collection_without_children: collectionId,
                                      with_unindexed: true,
                                      exclude_type: isPodcast ? 'podcast' : 'video',
                                  }
                                : {
                                      collection: collectionId,
                                      with_unindexed: true,
                                      order,
                                  }
                        }
                        itemsBlocks={[
                            {
                                type: 'items',
                                itemsType: 'documents',
                                presentation: 'grid',
                                cardWidth: 220,
                                cardTheme: 'rounded',
                                cardRatio: 'horizontal',
                                cardSize: 'responsive',
                                // An ad after every 8 items: 2 rows of 4 on desktop
                                count: 8,
                                multiplier: 8,
                                minColumns: 2,
                                ...(isPodcast && !hasSeasons
                                    ? {
                                          cardType: 'horizontal',
                                          cardTheme: 'box-translucent',
                                          cardRatio: 'square',
                                          cardSize: 'medium',
                                          autoColumns: true,
                                          cardWidth: 300,
                                      }
                                    : null),
                            },
                        ]}
                        disabled={collectionId === null}
                        className={styles.blocks}
                        // withoutMaxWidth
                        cardWithout={[
                            'serie',
                            'collection',
                            !isPodcast ? 'time' : null,
                            'author',
                            'type-icon',
                            'blocked-icon',
                        ].filter((n) => n !== null)}
                        cardWith={['episode']}
                    />
                ) : null}
            </main>
        </DocumentTheme>
    );
}

export default SerieCollection;
